/**
 * Shared constants for the seven-category scoring system, contribution tiers,
 * pre-publish checklist, and disclosure language.
 *
 * Mirrors the MyPickyFork Operations & Brand Manual v2.0. Keep this file as
 * the single source of truth in the admin; the public site copy should match.
 */

export type CategoryKey =
  | "flavor"
  | "seasoning"
  | "texture"
  | "presentation"
  | "value"
  | "service"
  | "would_return";

export interface CategoryDef {
  key: CategoryKey;
  label: string;
  weightLabel: "Most important" | "High" | "Medium" | "Tiebreaker";
  weight: number;
  ask: string;
}

/**
 * Weighted scoring (v1). Weights sum to 1.0.
 * Flavor leads. Seasoning, texture, and value for price are high. Presentation
 * and service are medium. Would-I-return is a tiebreaker carrying smaller weight
 * but still factored into the final number.
 */
export const CATEGORIES: readonly CategoryDef[] = [
  {
    key: "flavor",
    label: "Flavor",
    weightLabel: "Most important",
    weight: 0.25,
    ask: "Ignoring everything else, does this food taste good?",
  },
  {
    key: "seasoning",
    label: "Seasoning",
    weightLabel: "High",
    weight: 0.18,
    ask: "Was this dish properly seasoned, or did it taste flat, bland, or unbalanced?",
  },
  {
    key: "texture",
    label: "Texture",
    weightLabel: "High",
    weight: 0.15,
    ask: "Did the food feel right in my mouth?",
  },
  {
    key: "presentation",
    label: "Presentation",
    weightLabel: "Medium",
    weight: 0.08,
    ask: "When this plate arrived, did it look like someone took pride in it?",
  },
  {
    key: "value",
    label: "Value for price",
    weightLabel: "High",
    weight: 0.15,
    ask: "Would I pay this price again knowing exactly what I was going to get?",
  },
  {
    key: "service",
    label: "Service",
    weightLabel: "Medium",
    weight: 0.09,
    ask: "Did the people working here make the experience better or worse?",
  },
  {
    key: "would_return",
    label: "Would I return",
    weightLabel: "Tiebreaker",
    weight: 0.1,
    ask: "If someone asked me to go back next week, would I say yes?",
  },
] as const;

export const SCORE_WEIGHTS_VERSION = "weights_v1";

export type ReturnVerdict = "yes" | "no" | "maybe";

export type PriceRange = "$" | "$$" | "$$$";

export type ContributionTier = "standard" | "priority" | "full_meal" | "custom";

export interface ContributionTierDef {
  key: ContributionTier;
  label: string;
  amountLabel: string;
  sla: string;
}

export const CONTRIBUTION_TIERS: readonly ContributionTierDef[] = [
  {
    key: "standard",
    label: "Standard",
    amountLabel: "Free",
    sla: "General queue, no timeline guaranteed.",
  },
  {
    key: "priority",
    label: "Priority",
    amountLabel: "$15",
    sla: "Reviewed within 30 days; moves ahead of standard.",
  },
  {
    key: "full_meal",
    label: "Full meal",
    amountLabel: "$37.50",
    sla: "Reviewed within 14 days; priority placement on site.",
  },
  {
    key: "custom",
    label: "Custom",
    amountLabel: "Custom amount",
    sla: "Reader-chosen contribution within site limits.",
  },
] as const;

export interface ScoreLegendBand {
  min: number;
  max: number;
  label: string;
}

export const SCORE_LEGEND: readonly ScoreLegendBand[] = [
  { min: 9.0, max: 10.0, label: "Exceptional. Will go out of my way for this." },
  { min: 7.5, max: 8.9, label: "Solid. Worth your time and money." },
  { min: 6.0, max: 7.4, label: "Decent. Has potential but something is off." },
  { min: 4.0, max: 5.9, label: "Below average. Issues outweigh the good." },
  { min: 0, max: 3.9, label: "Skip it. Something went seriously wrong." },
] as const;

/**
 * Per the manual: any final score below 6.0 is considered negative and requires
 * at least two visits on separate occasions before publishing.
 */
export const NEGATIVE_REVIEW_THRESHOLD = 6.0;
export const NEGATIVE_REVIEW_MIN_VISITS = 2;

export interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
}

/**
 * Pre-publish checklist mirroring the operations manual §04.
 */
export const PRE_PUBLISH_CHECKLIST: readonly ChecklistItem[] = [
  {
    id: "paid_or_disclosed",
    label: "Meal was paid for out of pocket, or a disclosure is written.",
    required: true,
  },
  {
    id: "visits_completed",
    label: "Place was visited at least once (twice for a negative review).",
    required: true,
  },
  {
    id: "all_scores",
    label: "All seven categories are scored.",
    required: true,
  },
  {
    id: "verdict_one_sentence",
    label: "Verdict is written as one sentence.",
    required: true,
  },
  {
    id: "real_photos",
    label: "Photos are of the actual food received, not marketing images.",
    required: true,
  },
  {
    id: "final_score_correct",
    label: "Final score is calculated and looks right.",
    required: true,
  },
  {
    id: "header_complete",
    label: "Header info is complete (address, price, date, full order list).",
    required: true,
  },
  {
    id: "read_aloud",
    label: "Review was read aloud once before publishing.",
    required: false,
  },
  {
    id: "no_undisclosed_relationship",
    label: "No undisclosed personal relationship with the owner or staff.",
    required: true,
  },
  {
    id: "contribution_disclosed",
    label: "Disclosure line added if any external funding was received.",
    required: true,
  },
] as const;

export const READER_DISCLOSURE_TEXT =
  "This restaurant was submitted for review by a reader who contributed toward the meal cost. The contribution covered expenses only. The score, verdict, and all written content are entirely independent and were not influenced by the submission.";

/** Suggested values for combobox fields — operators can always type a custom value. */
export const CUISINE_PRESETS: readonly string[] = [
  "American",
  "BBQ",
  "Bakery",
  "Cafe",
  "Chinese",
  "French",
  "Indian",
  "Italian",
  "Japanese",
  "Korean",
  "Mediterranean",
  "Mexican",
  "Pizza",
  "Seafood",
  "Southern",
  "Steakhouse",
  "Thai",
  "Vietnamese",
] as const;

export const CITY_PRESETS: readonly string[] = [
  "Raleigh",
  "Durham",
  "Chapel Hill",
  "Cary",
  "Apex",
  "Holly Springs",
  "Morrisville",
  "Wake Forest",
  "Garner",
  "Fuquay-Varina",
  "Clayton",
  "Smithfield",
] as const;

export const NEIGHBORHOOD_PRESETS: readonly string[] = [
  "Downtown",
  "North Hills",
  "Glenwood South",
  "Cameron Village",
  "Five Points",
  "Warehouse District",
  "Crabtree",
  "Brier Creek",
  "South Durham",
  "Downtown Durham",
  "Franklin Street",
  "Southern Village",
] as const;
